/**
 * Feature Flags Configuration
 * Centralized feature flag management for the TI-M Web App
 * Allows runtime control of features and gradual rollout
 */

import logger from '../utils/logger';

// Get environment safely - webpack will replace these at build time
const NODE_ENV = typeof process !== 'undefined' && process.env?.NODE_ENV ? process.env.NODE_ENV : 'development';
const IS_DEVELOPMENT = NODE_ENV === 'development';
const IS_PRODUCTION = NODE_ENV === 'production';

// Get environment variables at build time (webpack DefinePlugin replacements)
const ENV_VARS = {
  REACT_APP_RECOVERY_FLOW_ENABLED: typeof process !== 'undefined' && process.env?.REACT_APP_RECOVERY_FLOW_ENABLED ? process.env.REACT_APP_RECOVERY_FLOW_ENABLED : undefined,
  REACT_APP_ENABLE_RUST_CRYPTO: typeof process !== 'undefined' && process.env?.REACT_APP_ENABLE_RUST_CRYPTO ? process.env.REACT_APP_ENABLE_RUST_CRYPTO : undefined,
  REACT_APP_PERSISTENT_STORAGE: typeof process !== 'undefined' && process.env?.REACT_APP_PERSISTENT_STORAGE ? process.env.REACT_APP_PERSISTENT_STORAGE : undefined,
  REACT_APP_DEBUG_MODE: typeof process !== 'undefined' && process.env?.REACT_APP_DEBUG_MODE ? process.env.REACT_APP_DEBUG_MODE : undefined,
  REACT_APP_SSO_ENABLED: typeof process !== 'undefined' && process.env?.REACT_APP_SSO_ENABLED ? process.env.REACT_APP_SSO_ENABLED : undefined,
  REACT_APP_AUTO_RECOVERY: typeof process !== 'undefined' && process.env?.REACT_APP_AUTO_RECOVERY ? process.env.REACT_APP_AUTO_RECOVERY : undefined,
  REACT_APP_PERFORMANCE_MONITORING: typeof process !== 'undefined' && process.env?.REACT_APP_PERFORMANCE_MONITORING ? process.env.REACT_APP_PERFORMANCE_MONITORING : undefined,
  REACT_APP_DEVICE_TRUST_AUTOMATION: typeof process !== 'undefined' && process.env?.REACT_APP_DEVICE_TRUST_AUTOMATION ? process.env.REACT_APP_DEVICE_TRUST_AUTOMATION : undefined,
};

/**
 * Feature flag definitions with defaults
 */
const DEFAULT_FLAGS = {
  recoveryFlow: {
    name: 'Recovery Flow',
    description: 'Enable recovery key flow for E2E encryption',
    enabled: true,
    envVar: 'REACT_APP_RECOVERY_FLOW_ENABLED',
  },
  rustCrypto: {
    name: 'Rust Crypto',
    description: 'Use Matrix Rust crypto implementation',
    enabled: true,
    envVar: 'REACT_APP_ENABLE_RUST_CRYPTO',
  },
  persistentStorage: {
    name: 'Persistent Storage',
    description: 'Request persistent storage permission',
    enabled: true,
    envVar: 'REACT_APP_PERSISTENT_STORAGE',
  },
  debugMode: {
    name: 'Debug Mode',
    description: 'Enable debug features and verbose logging',
    enabled: IS_DEVELOPMENT,
    envVar: 'REACT_APP_DEBUG_MODE',
  },
  ssoAuth: {
    name: 'SSO Authentication',
    description: 'Enable Single Sign-On authentication',
    enabled: false,
    envVar: 'REACT_APP_SSO_ENABLED',
  },
  autoRecovery: {
    name: 'Auto Recovery',
    description: 'Automatically attempt recovery on login',
    enabled: false,
    envVar: 'REACT_APP_AUTO_RECOVERY',
  },
  performanceMonitoring: {
    name: 'Performance Monitoring',
    description: 'Enable performance metrics collection',
    enabled: IS_PRODUCTION,
    envVar: 'REACT_APP_PERFORMANCE_MONITORING',
  },
  deviceTrustAutomation: {
    name: 'Device Trust Automation',
    description: 'Automatically verify device after successful recovery',
    enabled: true,
    envVar: 'REACT_APP_DEVICE_TRUST_AUTOMATION',
  },
};

/**
 * FeatureFlags class for managing feature toggles
 */
class FeatureFlags {
  constructor() {
    this.flags = this.loadFlags();
    this.overrides = this.loadOverrides();
    this.listeners = new Map();

    logger.info('Feature flags initialized', this.getAllFlags());
  }

  /**
   * Load flags from environment variables and defaults
   */
  loadFlags() {
    const flags = {};

    for (const [key, config] of Object.entries(DEFAULT_FLAGS)) {
      const envValue = ENV_VARS[config.envVar];
      let enabled = config.enabled;

      if (envValue !== undefined) {
        enabled = envValue === 'true';
        logger.debug(`Feature flag '${key}' set from env: ${enabled}`);
      }

      flags[key] = {
        ...config,
        enabled,
      };
    }

    return flags;
  }

  /**
   * Load user overrides from localStorage
   */
  loadOverrides() {
    try {
      const stored = localStorage.getItem('TI-M_FEATURE_FLAGS');
      if (stored) {
        const overrides = JSON.parse(stored);
        logger.debug('Loaded feature flag overrides from localStorage', overrides);
        return overrides;
      }
    } catch (error) {
      logger.warn('Failed to load feature flag overrides', error);
    }
    return {};
  }

  /**
   * Save overrides to localStorage
   */
  saveOverrides() {
    try {
      localStorage.setItem('TI-M_FEATURE_FLAGS', JSON.stringify(this.overrides));
      logger.debug('Saved feature flag overrides to localStorage');
    } catch (error) {
      logger.warn('Failed to save feature flag overrides', error);
    }
  }

  /**
   * Check if a feature is enabled
   * @param {string} feature - Feature key
   * @returns {boolean}
   */
  isEnabled(feature) {
    // Check for override first
    if (this.overrides[feature] !== undefined) {
      return this.overrides[feature];
    }

    // Fall back to configured value
    const flag = this.flags[feature];
    if (!flag) {
      logger.warn(`Unknown feature flag: ${feature}`);
      return false;
    }

    return flag.enabled;
  }

  /**
   * Set a feature flag override
   * @param {string} feature - Feature key
   * @param {boolean} enabled - Whether to enable the feature
   */
  setOverride(feature, enabled) {
    if (!this.flags[feature]) {
      logger.error(`Cannot override unknown feature: ${feature}`);
      return;
    }

    const previousValue = this.isEnabled(feature);
    this.overrides[feature] = enabled;
    this.saveOverrides();

    logger.info(`Feature flag '${feature}' overridden to: ${enabled}`);

    // Notify listeners if value changed
    if (previousValue !== enabled) {
      this.notifyListeners(feature, enabled);
    }
  }

  /**
   * Clear an override for a feature
   * @param {string} feature - Feature key
   */
  clearOverride(feature) {
    delete this.overrides[feature];
    this.saveOverrides();
    logger.info(`Feature flag override cleared for '${feature}'`);

    const currentValue = this.isEnabled(feature);
    this.notifyListeners(feature, currentValue);
  }

  /**
   * Clear all overrides
   */
  clearAllOverrides() {
    const previousOverrides = { ...this.overrides };
    this.overrides = {};
    this.saveOverrides();
    logger.info('All feature flag overrides cleared');

    // Notify listeners for each changed flag
    for (const feature of Object.keys(previousOverrides)) {
      const currentValue = this.isEnabled(feature);
      this.notifyListeners(feature, currentValue);
    }
  }

  /**
   * Get all feature flags with their current values
   * @returns {Object}
   */
  getAllFlags() {
    const result = {};
    for (const [key, config] of Object.entries(this.flags)) {
      result[key] = {
        ...config,
        enabled: this.isEnabled(key),
        overridden: this.overrides[key] !== undefined,
      };
    }
    return result;
  }

  /**
   * Register a listener for feature flag changes
   * @param {string} feature - Feature key
   * @param {Function} callback - Callback function (enabled: boolean) => void
   * @returns {Function} Unsubscribe function
   */
  onChange(feature, callback) {
    if (!this.listeners.has(feature)) {
      this.listeners.set(feature, new Set());
    }

    this.listeners.get(feature).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(feature);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(feature);
        }
      }
    };
  }

  /**
   * Notify listeners of feature flag change
   * @param {string} feature - Feature key
   * @param {boolean} enabled - New value
   */
  notifyListeners(feature, enabled) {
    const callbacks = this.listeners.get(feature);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(enabled);
        } catch (error) {
          logger.error(`Error in feature flag listener for '${feature}'`, error);
        }
      });
    }
  }

  /**
   * Check if running in debug mode
   * @returns {boolean}
   */
  isDebugMode() {
    return this.isEnabled('debugMode');
  }

  /**
   * Check if recovery flow is enabled
   * @returns {boolean}
   */
  isRecoveryFlowEnabled() {
    return this.isEnabled('recoveryFlow');
  }

  /**
   * Check if Rust crypto is enabled
   * @returns {boolean}
   */
  isRustCryptoEnabled() {
    return this.isEnabled('rustCrypto');
  }

  /**
   * Check if device trust automation is enabled
   * @returns {boolean}
   */
  isDeviceTrustAutomationEnabled() {
    return this.isEnabled('deviceTrustAutomation');
  }

  /**
   * Get feature flag for A/B testing or gradual rollout
   * @param {string} feature - Feature key
   * @param {number} percentage - Rollout percentage (0-100)
   * @param {string} userId - User identifier for consistent bucketing
   * @returns {boolean}
   */
  isEnabledForUser(feature, percentage, userId) {
    if (!this.isEnabled(feature)) {
      return false;
    }

    // Simple hash-based bucketing for consistent assignment
    let hash = 0;
    const str = `${feature}:${userId}`;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    const bucket = Math.abs(hash) % 100;
    const enabled = bucket < percentage;

    logger.debug(`Feature '${feature}' for user '${userId}': bucket=${bucket}, percentage=${percentage}, enabled=${enabled}`);
    return enabled;
  }

  /**
   * Export configuration for debugging
   * @returns {Object}
   */
  exportConfig() {
    return {
      flags: this.getAllFlags(),
      overrides: this.overrides,
      environment: NODE_ENV,
      timestamp: new Date().toISOString(),
    };
  }
}

// Create singleton instance
const featureFlags = new FeatureFlags();

// Export for use in React components
export default featureFlags;

// Named exports for convenience
export const isRecoveryFlowEnabled = () => featureFlags.isRecoveryFlowEnabled();
export const isRustCryptoEnabled = () => featureFlags.isRustCryptoEnabled();
export const isDebugMode = () => featureFlags.isDebugMode();
export const isDeviceTrustAutomationEnabled = () => featureFlags.isDeviceTrustAutomationEnabled();
export const isEnabled = (feature) => featureFlags.isEnabled(feature);